import { create } from "zustand";
import type { TopicListItem } from "../../types";
import { api } from "@/api/client";

interface TopicsStore {
  topics: TopicListItem[];
  loading: boolean;
  error: string | null;
  /** Loads the workspace's topics (non-archived; both arena and pulse kinds). */
  fetch: () => Promise<void>;
  /** Alias of fetch — used by mutating flows to re-pull after a change. */
  refresh: () => Promise<void>;
}

/**
 * The workspace's topics for the sidebar nav and topic pages. Stores ALL
 * non-archived topics (arena + pulse); the Sidebar filters to arena for its
 * list and the Brand Health pulse section reads the pulse one from here.
 *
 * Refetch-on-workspace-switch is driven by the Sidebar, which lives outside the
 * per-view remount boundary: it calls fetch() in an effect keyed on the active
 * workspace id, the same pattern the unread-alerts badge already uses. (Other
 * per-view stores like brands rely on per-mount fetch, but the sidebar persists
 * across switches, so it owns the reload.)
 */
export const useTopicsStore = create<TopicsStore>((set, get) => ({
  topics: [],
  loading: false,
  error: null,

  fetch: async () => {
    set({ loading: true });
    try {
      const topics = await api.topics.list();
      set({ topics, loading: false, error: null });
    } catch (err: any) {
      set({ loading: false, error: err?.message || "Failed to load topics" });
    }
  },

  refresh: async () => {
    await get().fetch();
  },
}));
